/**
 * Fixed-date recurrence (spec §9.5 last paragraph, D-8 clone-on-done): the small RRULE subset the *Repeats* control
 * writes and importers map to — FREQ=DAILY|WEEKLY|MONTHLY|YEARLY, INTERVAL, BYDAY (weekly), and for monthly rules
 * BYMONTH (which months), BYMONTHDAY (the nth or last day) or BYDAY+BYSETPOS (the nth or last workday or
 * non-workday; workdays are Monday to Friday); BYHOUR+BYMINUTE for the time the next one appears. Mirrored by
 * `server/drang/core/rrule.py`; both pass `tests/vectors/rrule.json`.
 */
const DAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const WORKDAYS = [1, 2, 3, 4, 5];
const NON_WORKDAYS = [0, 6];

export type NthKind = "day" | "workday" | "nonworkday";

export interface Rule {
  freq: "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
  interval: number;
  /** weekly: the weekdays, 0 = Sunday */
  byDay: number[];
  /** monthly: the months it happens in, 1–12; empty = every month */
  byMonth?: number[];
  /** monthly: on the nth (1…31, or 1…5 for workdays) or the last (-1) day of the kind */
  nth?: { kind: NthKind; n: number } | null;
  /** the time of day the next one appears; null = the time of the previous one */
  time?: { h: number; m: number } | null;
}

const sameSet = (a: number[], b: number[]) => a.length === b.length && a.every((x) => b.includes(x));

export function parseRrule(rrule: string): Rule | null {
  const parts = Object.fromEntries(
    rrule
      .replace(/^RRULE:/i, "")
      .split(";")
      .map((p) => p.split("=").map((x) => x.trim().toUpperCase()) as [string, string]),
  );
  const freq = parts.FREQ;
  if (freq !== "DAILY" && freq !== "WEEKLY" && freq !== "MONTHLY" && freq !== "YEARLY") return null;
  const interval = Math.max(1, Number(parts.INTERVAL ?? 1) || 1);
  const days = (parts.BYDAY ?? "")
    .split(",")
    .map((d) => DAYS.indexOf(d))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
  const rule: Rule = {
    freq,
    interval,
    byDay: freq === "MONTHLY" ? [] : days,
    byMonth: [],
    nth: null,
    time: null,
  };
  if (freq === "MONTHLY") {
    rule.byMonth = [...new Set((parts.BYMONTH ?? "").split(",").map(Number))]
      .filter((m) => Number.isInteger(m) && m >= 1 && m <= 12)
      .sort((a, b) => a - b);
    const monthDay = Number(parts.BYMONTHDAY);
    const pos = Number(parts.BYSETPOS);
    if (parts.BYMONTHDAY && (monthDay === -1 || (monthDay >= 1 && monthDay <= 31)))
      rule.nth = { kind: "day", n: monthDay };
    else if (parts.BYSETPOS && (pos === -1 || (pos >= 1 && pos <= 5)))
      if (sameSet(days, WORKDAYS)) rule.nth = { kind: "workday", n: pos };
      else if (sameSet(days, NON_WORKDAYS)) rule.nth = { kind: "nonworkday", n: pos };
  }
  const h = Number(parts.BYHOUR);
  const m = Number(parts.BYMINUTE ?? 0);
  if (parts.BYHOUR && Number.isInteger(h) && h >= 0 && h <= 23 && Number.isInteger(m) && m >= 0 && m <= 59)
    rule.time = { h, m };
  return rule;
}

/** The rule as stored in `repeat.rrule` (the inverse of `parseRrule`; weekdays only for weekly rules). */
export function formatRrule(rule: Rule): string {
  const parts = [`FREQ=${rule.freq}`];
  if (rule.interval > 1) parts.push(`INTERVAL=${Math.round(rule.interval)}`);
  if (rule.freq === "WEEKLY" && rule.byDay.length)
    parts.push(
      `BYDAY=${[...new Set(rule.byDay)]
        .sort((a, b) => a - b)
        .map((d) => DAYS[d])
        .join(",")}`,
    );
  if (rule.freq === "MONTHLY") {
    if (rule.byMonth?.length)
      parts.push(`BYMONTH=${[...new Set(rule.byMonth)].sort((a, b) => a - b).join(",")}`);
    const nth = rule.nth;
    if (nth?.kind === "day") parts.push(`BYMONTHDAY=${nth.n}`);
    else if (nth)
      parts.push(
        `BYDAY=${(nth.kind === "workday" ? WORKDAYS : NON_WORKDAYS).map((d) => DAYS[d]).join(",")}`,
        `BYSETPOS=${nth.n}`,
      );
  }
  if (rule.time) parts.push(`BYHOUR=${rule.time.h}`, `BYMINUTE=${rule.time.m}`);
  return parts.join(";");
}

/** Does day `d` (any time) fit a monthly rule with months or an nth day, counted from the anchor's month? */
function fitsMonthly(rule: Rule, anchor: Date, d: Date): boolean {
  const months = (d.getFullYear() - anchor.getFullYear()) * 12 + (d.getMonth() - anchor.getMonth());
  if (months < 0 || months % rule.interval !== 0) return false;
  if (rule.byMonth?.length && !rule.byMonth.includes(d.getMonth() + 1)) return false;
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const nth = rule.nth;
  if (!nth) return d.getDate() === anchor.getDate();
  if (nth.kind === "day") return d.getDate() === (nth.n === -1 ? last : nth.n);
  const kinds = nth.kind === "workday" ? WORKDAYS : NON_WORKDAYS;
  const matching: number[] = [];
  for (let day = 1; day <= last; day++)
    if (kinds.includes(new Date(d.getFullYear(), d.getMonth(), day).getDay())) matching.push(day);
  const pick = nth.n === -1 ? matching[matching.length - 1] : matching[nth.n - 1];
  return d.getDate() === pick;
}

/** The first occurrence strictly after `after`, at the rule's time or else the time of day of `anchor`. */
export function nextOccurrence(rrule: string, anchor: Date, after: Date): Date | null {
  const rule = parseRrule(rrule);
  if (!rule) return null;
  const at = (d: Date) => {
    const x = new Date(d);
    if (rule.time) x.setHours(rule.time.h, rule.time.m, 0, 0);
    else x.setHours(anchor.getHours(), anchor.getMinutes(), 0, 0);
    return x;
  };
  if (rule.freq === "WEEKLY" && rule.byDay.length) {
    const d = at(after);
    for (let i = 0; i < 7 * rule.interval + 7; i++) {
      d.setDate(d.getDate() + (i === 0 ? 0 : 1));
      if (d <= after) continue;
      // whole days between the two Mondays: a week with the spring clock change is an hour short, and dividing
      // milliseconds made it a week too few, so the repeat came a week early (bug hunt M9; rrule.py counts dates)
      const days = Math.round((startOfWeek(d).getTime() - startOfWeek(anchor).getTime()) / 86_400_000);
      const weeks = Math.floor(days / 7);
      if (rule.byDay.includes(d.getDay()) && ((weeks % rule.interval) + rule.interval) % rule.interval === 0)
        return d;
    }
    return null;
  }
  if (rule.freq === "MONTHLY" && (rule.byMonth?.length || rule.nth)) {
    // day by day: at most every 12 months times the interval, plus a year for months not chosen
    const d = at(after);
    for (let i = 0; i < 366 * (rule.interval + 1) + 31; i++) {
      d.setDate(d.getDate() + (i === 0 ? 0 : 1));
      if (d > after && fitsMonthly(rule, anchor, d)) return at(d);
    }
    return null;
  }
  const d = at(anchor);
  for (let i = 0; i < 5000 && d <= after; i++) {
    if (rule.freq === "DAILY") d.setDate(d.getDate() + rule.interval);
    else if (rule.freq === "WEEKLY") d.setDate(d.getDate() + 7 * rule.interval);
    else if (rule.freq === "MONTHLY") d.setMonth(d.getMonth() + rule.interval);
    else d.setFullYear(d.getFullYear() + rule.interval);
  }
  return d > after ? d : null;
}

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
